import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProgressionSuggestions from '../components/ProgressionSuggestions';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Dumbbell,
  Heart,
  Zap,
  Wind,
  Moon,
  Calendar as CalendarIcon,
  Clock,
  Check,
  X,
  GripVertical,
  AlertCircle,
  TrendingUp,
  Battery
} from 'lucide-react';

const WORKOUT_TYPES = [
  { id: 'strength', label: 'Strength', icon: Dumbbell, color: 'bg-red-500' },
  { id: 'cardio', label: 'Cardio', icon: Heart, color: 'bg-blue-500' },
  { id: 'hiit', label: 'HIIT', icon: Zap, color: 'bg-yellow-500' },
  { id: 'yoga', label: 'Yoga', icon: Wind, color: 'bg-green-500' },
  { id: 'rest', label: 'Rest Day', icon: Moon, color: 'bg-purple-500' }
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarPage() {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [restRecommendations, setRestRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Form state
  const [newWorkout, setNewWorkout] = useState({
    title: '',
    workout_type: 'strength',
    duration_minutes: 45,
    scheduled_time: '09:00'
  });

  const fetchData = useCallback(async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    
    try {
      const [schedulesRes, statsRes, restRes] = await Promise.all([
        axios.get(`${API}/calendar/month/${year}/${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/calendar/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/calendar/rest-recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSchedules(schedulesRes.data);
      setStats(statsRes.data);
      setRestRecommendations(restRes.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        date: date.toISOString().split('T')[0],
        isToday: new Date().toDateString() === date.toDateString()
      });
    }
    
    return days;
  };

  const getSchedulesForDate = (date) => {
    if (!date) return [];
    return schedules.filter(s => s.scheduled_date === date);
  };

  const handleAddWorkout = async () => {
    if (!selectedDate || !newWorkout.title) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(
        `${API}/calendar/schedule`,
        null,
        {
          params: {
            title: newWorkout.title,
            scheduled_date: selectedDate,
            workout_type: newWorkout.workout_type,
            scheduled_time: newWorkout.scheduled_time,
            duration_minutes: newWorkout.duration_minutes
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Workout scheduled!');
      setAddDialogOpen(false);
      setNewWorkout({ title: '', workout_type: 'strength', duration_minutes: 45, scheduled_time: '09:00' });
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule workout');
    }
  };

  const handleCompleteWorkout = async (scheduleId) => {
    try {
      const response = await axios.post(
        `${API}/calendar/schedule/${scheduleId}/complete`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Workout completed! +${response.data.points_earned} points`);
      fetchData();
    } catch (error) {
      toast.error('Failed to complete workout');
    }
  };

  const handleSkipWorkout = async (scheduleId) => {
    try {
      await axios.post(
        `${API}/calendar/schedule/${scheduleId}/skip`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Workout skipped');
      fetchData();
    } catch (error) {
      toast.error('Failed to skip workout');
    }
  };

  const handleDeleteWorkout = async (scheduleId) => {
    try {
      await axios.delete(`${API}/calendar/schedule/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workout removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete workout');
    }
  };

  const handleScheduleRestDay = async (date) => {
    try {
      await axios.post(
        `${API}/calendar/rest-day`,
        null,
        {
          params: { date },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Rest day scheduled!');
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule rest day');
    }
  };

  const handleDragStart = (e, schedule) => {
    setDraggedItem(schedule);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newDate) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.scheduled_date === newDate) {
      setDraggedItem(null);
      return;
    }
    
    try {
      await axios.put(
        `${API}/calendar/schedule/${draggedItem.id}`,
        null,
        {
          params: { scheduled_date: newDate },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Workout rescheduled!');
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule workout');
    }
    setDraggedItem(null);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getWorkoutTypeInfo = (type) => {
    return WORKOUT_TYPES.find(t => t.id === type) || WORKOUT_TYPES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const days = getDaysInMonth();

  return (
    <div data-testid="calendar-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">WORKOUT</span> CALENDAR
          </h1>
          <p className="text-gray-400 mt-1">Plan and track your training schedule</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical text-center">
            <CalendarIcon className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.completed_this_month}</div>
            <div className="text-xs text-gray-400">Completed This Month</div>
          </div>
          <div className="card-tactical text-center">
            <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.current_streak}</div>
            <div className="text-xs text-gray-400">Day Streak</div>
          </div>
          <div className="card-tactical text-center">
            <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.total_minutes_this_month}</div>
            <div className="text-xs text-gray-400">Minutes Trained</div>
          </div>
          <div className="card-tactical text-center">
            <Dumbbell className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.upcoming_count}</div>
            <div className="text-xs text-gray-400">Upcoming Workouts</div>
          </div>
        </div>
      )}

      {/* Rest Day Recommendations */}
      {restRecommendations && (
        <div className={`card-tactical ${
          restRecommendations.fatigue_level === 'high' ? 'border-red-500/50' :
          restRecommendations.fatigue_level === 'moderate' ? 'border-yellow-500/50' :
          'border-green-500/50'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              restRecommendations.fatigue_level === 'high' ? 'bg-red-500/20' :
              restRecommendations.fatigue_level === 'moderate' ? 'bg-yellow-500/20' :
              'bg-green-500/20'
            }`}>
              <Battery className={`w-6 h-6 ${
                restRecommendations.fatigue_level === 'high' ? 'text-red-500' :
                restRecommendations.fatigue_level === 'moderate' ? 'text-yellow-500' :
                'text-green-500'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg text-white font-semibold flex items-center gap-2">
                  Recovery Status
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    restRecommendations.fatigue_level === 'high' ? 'bg-red-500/20 text-red-400' :
                    restRecommendations.fatigue_level === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {restRecommendations.fatigue_level.toUpperCase()}
                  </span>
                </h3>
                <div className="text-right">
                  <div className="text-2xl font-mono text-white">{restRecommendations.fatigue_score}</div>
                  <div className="text-xs text-gray-500">Fatigue Score</div>
                </div>
              </div>
              
              <div className="flex items-center gap-6 mt-2 text-sm text-gray-400">
                <span>{restRecommendations.workouts_this_week} workouts this week</span>
                <span>{restRecommendations.total_training_minutes} min trained</span>
                {restRecommendations.consecutive_workout_days > 0 && (
                  <span className="text-yellow-400">{restRecommendations.consecutive_workout_days} consecutive days</span>
                )}
              </div>
              
              {/* Fatigue Bar */}
              <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    restRecommendations.fatigue_level === 'high' ? 'bg-red-500' :
                    restRecommendations.fatigue_level === 'moderate' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(restRecommendations.fatigue_score, 100)}%` }}
                />
              </div>
              
              {restRecommendations.should_rest_today && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <p className="text-red-400 font-medium">⚠️ Rest recommended today</p>
                  <p className="text-sm text-gray-400">{restRecommendations.today_reason}</p>
                </div>
              )}
              
              {restRecommendations.recommendations?.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-gray-300 font-medium">Recommended Rest Days:</p>
                  {restRecommendations.recommendations.map((rec, i) => (
                    <div key={i} className="p-3 bg-secondary/50 rounded-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">{rec.reason}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            📅 {rec.day_name}, {new Date(rec.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {rec.priority === 'high' && (
                              <span className="ml-2 text-red-400">• High Priority</span>
                            )}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleScheduleRestDay(rec.date)}
                          className={`${
                            rec.priority === 'high' ? 'bg-red-500 hover:bg-red-600' :
                            'bg-yellow-500 hover:bg-yellow-600'
                          } text-black`}
                        >
                          <Moon className="w-4 h-4 mr-1" />
                          Schedule
                        </Button>
                      </div>
                      {rec.recovery_tips && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {rec.recovery_tips.slice(0, 3).map((tip, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-secondary rounded text-gray-400">
                              💡 {tip}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {restRecommendations.recommendations?.length === 0 && !restRecommendations.should_rest_today && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <p className="text-green-400">✓ You're well recovered! Keep up the great work.</p>
                  <p className="text-sm text-gray-400 mt-1">Your training load is balanced. Continue your planned workouts.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Progression Suggestions */}
      <ProgressionSuggestions 
        workoutId={schedules.find(s => s.scheduled_date === new Date().toISOString().split('T')[0])?.id}
        onApplied={() => fetchData()}
      />

      {/* Calendar */}
      <div className="card-tactical">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h2 className="text-xl text-white font-semibold">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map(day => (
            <div key={day} className="text-center text-sm text-gray-400 font-medium py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayInfo, index) => {
            const daySchedules = getSchedulesForDate(dayInfo.date);
            const hasRestRecommendation = restRecommendations?.recommendations.some(
              r => r.date === dayInfo.date
            );
            
            return (
              <div
                key={index}
                className={`min-h-[100px] p-2 rounded-sm border transition-colors ${
                  dayInfo.day
                    ? dayInfo.isToday
                      ? 'bg-primary/10 border-primary'
                      : 'bg-secondary/30 border-border hover:border-primary/50'
                    : 'bg-transparent border-transparent'
                }`}
                onDragOver={dayInfo.date ? handleDragOver : undefined}
                onDrop={dayInfo.date ? (e) => handleDrop(e, dayInfo.date) : undefined}
              >
                {dayInfo.day && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${
                        dayInfo.isToday ? 'text-primary' : 'text-gray-400'
                      }`}>
                        {dayInfo.day}
                      </span>
                      {hasRestRecommendation && (
                        <Moon className="w-3 h-3 text-yellow-500" />
                      )}
                      <Dialog open={addDialogOpen && selectedDate === dayInfo.date} onOpenChange={(open) => {
                        setAddDialogOpen(open);
                        if (open) setSelectedDate(dayInfo.date);
                      }}>
                        <DialogTrigger asChild>
                          <button
                            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-primary transition-colors"
                            onClick={() => setSelectedDate(dayInfo.date)}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="bg-card border-border">
                          <DialogHeader>
                            <DialogTitle className="text-white">
                              Schedule Workout - {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                            </DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-gray-300">Workout Title</Label>
                              <Input
                                value={newWorkout.title}
                                onChange={(e) => setNewWorkout({...newWorkout, title: e.target.value})}
                                placeholder="e.g., Upper Body Strength"
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-gray-300">Workout Type</Label>
                              <Select
                                value={newWorkout.workout_type}
                                onValueChange={(value) => setNewWorkout({...newWorkout, workout_type: value})}
                              >
                                <SelectTrigger className="bg-secondary border-transparent mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {WORKOUT_TYPES.map(type => (
                                    <SelectItem key={type.id} value={type.id}>
                                      <div className="flex items-center gap-2">
                                        <type.icon className="w-4 h-4" />
                                        {type.label}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-gray-300">Time</Label>
                                <Input
                                  type="time"
                                  value={newWorkout.scheduled_time}
                                  onChange={(e) => setNewWorkout({...newWorkout, scheduled_time: e.target.value})}
                                  className="bg-secondary border-transparent mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-300">Duration (min)</Label>
                                <Input
                                  type="number"
                                  value={newWorkout.duration_minutes}
                                  onChange={(e) => setNewWorkout({...newWorkout, duration_minutes: parseInt(e.target.value) || 45})}
                                  className="bg-secondary border-transparent mt-1"
                                />
                              </div>
                            </div>
                            
                            <Button onClick={handleAddWorkout} className="w-full bg-primary hover:bg-red-700">
                              Schedule Workout
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    
                    {/* Scheduled Workouts */}
                    <div className="space-y-1">
                      {daySchedules.map((schedule) => {
                        const typeInfo = getWorkoutTypeInfo(schedule.workout_type);
                        const TypeIcon = typeInfo.icon;
                        
                        return (
                          <div
                            key={schedule.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, schedule)}
                            className={`group relative p-1.5 rounded-sm text-xs cursor-move ${
                              schedule.status === 'completed' 
                                ? 'bg-green-500/20 text-green-400'
                                : schedule.status === 'skipped'
                                  ? 'bg-gray-500/20 text-gray-400 line-through'
                                  : `${typeInfo.color}/20 text-white`
                            }`}
                          >
                            <div className="flex items-center gap-1">
                              <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                              <TypeIcon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate flex-1">{schedule.title}</span>
                            </div>
                            
                            {/* Action buttons on hover */}
                            {schedule.status === 'scheduled' && (
                              <div className="absolute right-0 top-0 hidden group-hover:flex items-center gap-0.5 bg-card p-0.5 rounded-sm">
                                <button
                                  onClick={() => handleCompleteWorkout(schedule.id)}
                                  className="p-0.5 text-green-500 hover:bg-green-500/20 rounded"
                                  title="Complete"
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleSkipWorkout(schedule.id)}
                                  className="p-0.5 text-yellow-500 hover:bg-yellow-500/20 rounded"
                                  title="Skip"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {WORKOUT_TYPES.map(type => (
          <div key={type.id} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${type.color}`} />
            <span className="text-gray-400">{type.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-gray-400">Completed</span>
        </div>
      </div>
    </div>
  );
}
